import { useState, useEffect } from 'react';
import { loadTheme, getDefaultTheme } from '../themes.js';

export function useTheme() {
  const [currentTheme, setCurrentTheme] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load theme from env, then localStorage, then default to 'antic'
  const getInitialTheme = () => {
    // 1. Check VITE_THEME env variable (build-time)
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_THEME) {
      return import.meta.env.VITE_THEME;
    }
    // 2. Check localStorage
    try {
      const stored = localStorage.getItem('versatiles-theme');
      if (stored) return stored;
    } catch (e) {}
    // 3. Fallback to 'antic'
    return 'antic';
  };

  // Save theme to localStorage
  const saveTheme = (themeId) => {
    try {
      localStorage.setItem('versatiles-theme', themeId);
    } catch (e) {
      console.warn('Could not save theme to localStorage:', e);
    }
  };

  // Switch to a new theme
  const switchTheme = async (themeId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log('Switching to theme:', themeId);
      const theme = await loadTheme(themeId);
      console.log('Loaded theme:', theme);
      
      // Ensure the theme object has the correct ID
      const themeWithId = {
        ...theme,
        id: themeId
      };
      
      // Apply theme tokens based on theme ID
      if (themeId === 'antic' && theme.tokens?.applyAnticTheme) {
        console.log('Applying Antic theme...');
        theme.tokens.applyAnticTheme();
      } else if (themeId === 'default' && theme.tokens?.applyDefaultTheme) {
        console.log('Applying Default theme...');
        theme.tokens.applyDefaultTheme();
      } else {
        console.warn('No theme application function found for:', themeId);
      }
      
      // Load theme CSS if it exists
      try {
        await import(`../stores/${themeId}/overrides.css`);
        console.log('Loaded CSS overrides for theme:', themeId);
      } catch (cssError) {
        console.log('No CSS overrides found for theme:', themeId);
      }
      
      setCurrentTheme(themeWithId);
      saveTheme(themeId);
      console.log('Theme switched successfully to:', themeId, 'Theme object:', themeWithId);
    } catch (err) {
      console.error('Failed to switch theme:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize theme on mount
  useEffect(() => {
    const initTheme = async () => {
      const initialThemeId = getInitialTheme();
      await switchTheme(initialThemeId);
    };
    initTheme();
  }, []);

  return {
    currentTheme,
    switchTheme,
    isLoading,
    error
  };
}

export default useTheme;
